import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery, getTrendStats } from 'wasp/client/operations';
import { Button } from '../components/ui';
import { getBrowserOrigin, shareOrCopyUrl } from '../client/shareLinks';
import {
  buildAbsoluteShareUrl,
  buildTrendsSharePath,
} from '../shared/shareRoutes';
import useColorMode from '../client/hooks/useColorMode';
import TimeRangeToggle from './components/TimeRangeToggle';
import TrendChart from './components/TrendChart';
import CategoryChart from './components/CategoryChart';
import ProviderComparisonChart from './components/ProviderComparisonChart';
import type { TrendRange } from './types';

function useCountUp(target: number, duration = 600): number {
  const [value, setValue] = useState(0);
  const prevTarget = useRef<number | null>(null);
  const currentValue = useRef(0);

  useEffect(() => {
    if (target === prevTarget.current) return;
    const from = currentValue.current;
    prevTarget.current = target;
    const start = performance.now();
    let rafId: number;

    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      const next = Math.round(from + (target - from) * eased);
      currentValue.current = next;
      setValue(next);
      if (progress < 1) rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [target, duration]);

  return value;
}

export function TrendsPage() {
  const [range, setRange] = useState<TrendRange>('30d');
  const [shareFeedback, setShareFeedback] = useState<string | null>(null);
  const shareFeedbackTimer = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    return () => {
      if (shareFeedbackTimer.current) clearTimeout(shareFeedbackTimer.current);
    };
  }, []);

  const showShareFeedback = (msg: string) => {
    if (shareFeedbackTimer.current) clearTimeout(shareFeedbackTimer.current);
    setShareFeedback(msg);
    shareFeedbackTimer.current = setTimeout(() => setShareFeedback(null), 2000);
  };

  const [colorMode] = useColorMode();
  const isDark = colorMode === 'dark';

  const { data, isLoading, error, refetch } = useQuery(
    getTrendStats,
    { range },
    { staleTime: 5 * 60 * 1000 },
  );

  const isEmpty = !isLoading && !error && data && data.totalArticles === 0;

  const handleShareClick = () => {
    void shareOrCopyUrl({
      title: 'Positive Trends - Optimistic News',
      text: 'Share the evergreen positive-trends preview from Optimistic News.',
      url: buildAbsoluteShareUrl(getBrowserOrigin(), buildTrendsSharePath()),
    })
      .then((result) => {
        showShareFeedback(result === 'copied' ? 'Share link copied' : 'Shared');
      })
      .catch(() => {
        showShareFeedback('Could not share trends');
      });
  };

  const positivityRate =
    data && data.fullDataset.totalArticles > 0
      ? Math.round((data.totalArticles / data.fullDataset.totalArticles) * 100)
      : 0;

  const animatedRate = useCountUp(positivityRate);

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://optimistic-news.com/trends" />
        <title>Positive News Trends - Optimistic News</title>
        <meta
          name="description"
          content="See data-backed evidence that good news is growing. Track positive news trends by category, sentiment, and volume over time."
        />
        <meta
          property="og:title"
          content="Positive News Trends - Optimistic News"
        />
        <meta
          property="og:description"
          content="See data-backed evidence that good news is growing. Track positive news trends by category, sentiment, and volume over time."
        />
        <meta property="og:url" content="https://optimistic-news.com/trends" />
      </Helmet>

      <div className="py-8 lg:py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Header — left-aligned title, controls pushed right */}
          <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                <span className="text-primary">Positive</span>
                <span className="text-neutral-900 dark:text-white">
                  {' '}
                  Trends
                </span>
              </h2>
              <p className="mt-1 text-neutral-500 dark:text-neutral-400">
                Data-backed evidence that good news is growing.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <TimeRangeToggle value={range} onChange={setRange} />
              <Button
                type="button"
                variant="outline"
                tone="primary"
                onClick={handleShareClick}
              >
                Share trends
              </Button>
              {shareFeedback && (
                <span
                  className="text-sm text-neutral-500 dark:text-neutral-300"
                  role="status"
                >
                  {shareFeedback}
                </span>
              )}
            </div>
          </div>

          {/* Loading skeleton — matches new layout shape */}
          {isLoading && (
            <div className="space-y-10">
              <div className="animate-pulse">
                <div className="mb-2 h-16 w-28 rounded-lg bg-neutral-200 dark:bg-neutral-700" />
                <div className="mb-1 h-5 w-48 rounded bg-neutral-200 dark:bg-neutral-700" />
                <div className="h-4 w-72 rounded bg-neutral-200 dark:bg-neutral-700" />
              </div>
              <div className="animate-pulse rounded-2xl bg-neutral-100 p-8 dark:bg-neutral-800/50">
                <div className="h-72 rounded-lg bg-neutral-200 dark:bg-neutral-700" />
              </div>
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                <div className="animate-pulse space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-7 rounded-full bg-neutral-200 dark:bg-neutral-700"
                      style={{ width: `${90 - i * 12}%` }}
                    />
                  ))}
                </div>
                <div className="animate-pulse space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-7 rounded-full bg-neutral-200 dark:bg-neutral-700"
                      style={{ width: `${80 - i * 15}%` }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {error && !isLoading && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <p className="mb-2 text-lg font-semibold text-danger-600 dark:text-danger-400">
                Failed to load trend data
              </p>
              <p className="mb-6 text-sm text-neutral-500 dark:text-neutral-400">
                {error.message}
              </p>
              <button
                type="button"
                onClick={() => refetch()}
                className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
              >
                Retry
              </button>
            </div>
          )}

          {isEmpty && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="mb-4 text-5xl">📈</div>
              <h3 className="mb-2 text-lg font-semibold text-neutral-700 dark:text-neutral-200">
                No data for this period
              </h3>
              <p className="text-neutral-500 dark:text-neutral-400">
                Try selecting a wider time range to see trends.
              </p>
            </div>
          )}

          {data && !isLoading && !error && data.totalArticles > 0 && (
            <>
              {/* Hero Insight — centered percentage with stats underneath */}
              <div className="mb-10 flex flex-col items-center text-center">
                <p className="text-6xl font-bold tracking-tight text-primary sm:text-7xl">
                  {animatedRate}
                  <span className="text-4xl sm:text-5xl">%</span>
                </p>
                <p className="mt-1 text-lg text-neutral-600 dark:text-neutral-300">
                  of articles are positive
                </p>
                <div className="mt-3 flex items-center divide-x divide-neutral-200 text-sm dark:divide-neutral-700">
                  <span className="pr-4 text-neutral-500 dark:text-neutral-400">
                    <span className="font-semibold tabular-nums text-neutral-700 dark:text-neutral-200">
                      {data.totalArticles.toLocaleString()}
                    </span>{' '}
                    of{' '}
                    <span className="tabular-nums">
                      {data.fullDataset.totalArticles.toLocaleString()}
                    </span>{' '}
                    articles
                  </span>
                  {data.providerCount > 0 && (
                    <span className="px-4 text-neutral-500 dark:text-neutral-400">
                      <span className="font-semibold text-neutral-700 dark:text-neutral-200">
                        {data.providerCount}
                      </span>{' '}
                      sources
                    </span>
                  )}
                  {data.topCategory && (
                    <span className="pl-4 text-neutral-500 dark:text-neutral-400">
                      Led by{' '}
                      <span className="font-semibold text-neutral-700 dark:text-neutral-200">
                        {data.topCategory}
                      </span>
                    </span>
                  )}
                </div>
              </div>

              {/* Trend Chart — the proof */}
              <section className="mb-12 rounded-2xl bg-neutral-50 p-5 dark:bg-neutral-800/40 sm:p-8">
                <h3 className="mb-1 text-lg font-semibold text-neutral-900 dark:text-white">
                  Articles Over Time
                </h3>
                <p className="mb-6 text-sm text-neutral-500 dark:text-neutral-400">
                  Positive articles vs total volume, with trend line
                </p>
                <TrendChart
                  data={data.timeSeries}
                  allData={data.fullDataset.timeSeries}
                  isDark={isDark}
                />
              </section>

              {/* Two-column: categories + sources */}
              <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
                <section>
                  <h3 className="mb-5 text-lg font-semibold text-neutral-900 dark:text-white">
                    Top Categories
                  </h3>
                  <CategoryChart data={data.categories} />
                </section>
                <section>
                  <h3 className="mb-5 text-lg font-semibold text-neutral-900 dark:text-white">
                    Source Quality
                  </h3>
                  <ProviderComparisonChart data={data.providerComparison} />
                </section>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
